import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  CheckCircle2, Circle, Lock, ClipboardList, ChevronDown, ChevronUp,
  Trophy, PlayCircle, VideoOff, Loader2,
} from "lucide-react";

/**
 * Riepilogo del percorso: moduli, lezioni e test, con lo stato di avanzamento.
 *
 * Riceve il `summary` di GET /me/courses/{id}/progress — gli stessi dati già usati
 * dalla pagina del corso: nessuna chiamata aggiuntiva, nessuno stato duplicato.
 *
 * Ogni voce è un "passaggio" del percorso: le lezioni e, dove definito, il test
 * del modulo. Il conteggio in testa usa la stessa nozione, così la percentuale
 * mostrata corrisponde a ciò che si vede nell'elenco.
 */

function StepIcon({ state }) {
  if (state === "completed") return <CheckCircle2 size={17} className="text-emerald-600 shrink-0" />;
  if (state === "locked") return <Lock size={16} className="text-brand-muted shrink-0" />;
  if (state === "current") return <PlayCircle size={17} className="text-brand-blue shrink-0" />;
  if (state === "no-video") return <VideoOff size={16} className="text-brand-muted shrink-0" />;
  return <Circle size={16} className="text-brand-muted shrink-0" />;
}

function lessonState(lesson, isCurrent) {
  if (lesson.is_completed) return "completed";
  if (lesson.is_locked) return "locked";
  if (lesson.has_video === false) return "no-video";
  return isCurrent ? "current" : "todo";
}

function lessonHint(lesson) {
  if (lesson.is_completed) return "Completata";
  if (lesson.is_locked) return lesson.lock_reason || "Bloccata";
  if (lesson.has_video === false) return "Video non disponibile";
  if (lesson.percent_watched > 0) return `Visto ${Math.round(lesson.percent_watched)}%`;
  return "Da iniziare";
}

/** Stato del test del modulo, con il messaggio da mostrare sotto la voce. */
function testState(mod) {
  if (mod.intermediate_passed) return { state: "completed", hint: "Superato" };
  if (mod.intermediate_locked) {
    return { state: "locked", hint: "Tentativi esauriti: rivedi le lezioni" };
  }
  if (mod.intermediate_test_unlocked) {
    const left = (mod.intermediate_attempts_max ?? 3) - (mod.intermediate_attempts_used ?? 0);
    return { state: "todo", hint: `Disponibile · ${left} tentativi rimasti`, clickable: true };
  }
  return { state: "locked", hint: "Completa le lezioni del modulo" };
}

export default function CourseOutline({
  summary,
  currentLessonId,
  courseId,
  onStartTest,
  startingTest,
}) {
  const navigate = useNavigate();
  const modules = summary.modules || [];

  // Il modulo della lezione in corso parte aperto; gli altri chiusi, così con
  // molti moduli l'elenco resta leggibile senza scorrere all'infinito.
  const currentModuleId = modules.find((m) =>
    m.lessons.some((l) => l.id === currentLessonId)
  )?.id;

  const [openIds, setOpenIds] = useState(() => new Set(currentModuleId ? [currentModuleId] : []));

  useEffect(() => {
    if (currentModuleId) {
      setOpenIds((prev) => new Set(prev).add(currentModuleId));
    }
  }, [currentModuleId]);

  const toggle = (id) =>
    setOpenIds((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });

  // Passaggi = lezioni + test definiti. Il test finale conta come passaggio conclusivo.
  let stepsTotal = 0;
  let stepsDone = 0;
  modules.forEach((m) => {
    stepsTotal += m.lessons.length;
    stepsDone += m.lessons.filter((l) => l.is_completed).length;
    if (m.has_intermediate_test) {
      stepsTotal += 1;
      if (m.intermediate_passed) stepsDone += 1;
    }
  });
  if (summary.has_final_test) {
    stepsTotal += 1;
    if (summary.final_test_passed) stepsDone += 1;
  }
  const pct = stepsTotal ? Math.round((stepsDone / stepsTotal) * 100) : 0;

  return (
    <div data-testid="course-outline" className="flex flex-col h-full bg-white">
      {/* Intestazione: corso e avanzamento complessivo */}
      <div className="bg-brand-blue text-white px-5 py-4 shrink-0">
        <div className="text-[10px] uppercase tracking-widest text-brand-gold mb-1">
          Percorso formativo
        </div>
        <h2 className="text-[15px] font-semibold leading-snug mb-3">{summary.title}</h2>
        <div className="flex items-center justify-between text-[11px] mb-1.5">
          <span className="font-semibold text-brand-gold" data-testid="outline-progress-pct">
            {pct}% completato
          </span>
          <span className="text-white/70" data-testid="outline-steps">
            {stepsDone}/{stepsTotal} passaggi
          </span>
        </div>
        <div className="h-1.5 bg-white/20 rounded-sm overflow-hidden">
          <div className="h-full bg-brand-gold transition-all" style={{ width: `${pct}%` }} />
        </div>
      </div>

      {/* Elenco moduli */}
      <nav className="flex-1 overflow-y-auto">
        {modules.map((mod, mi) => {
          const isOpen = openIds.has(mod.id);
          const done = mod.lessons.filter((l) => l.is_completed).length;
          const test = mod.has_intermediate_test ? testState(mod) : null;

          return (
            <div key={mod.id} className="border-b border-brand-line">
              <button
                type="button"
                onClick={() => toggle(mod.id)}
                data-testid={`outline-module-${mod.id}`}
                className="w-full text-left px-4 py-3 hover:bg-brand-bg transition-colors flex items-start gap-2.5"
              >
                <span className="mt-0.5">
                  {mod.module_completed && (!test || test.state === "completed") ? (
                    <CheckCircle2 size={17} className="text-emerald-600" />
                  ) : (
                    <Circle size={16} className="text-brand-muted" />
                  )}
                </span>
                <span className="flex-1 min-w-0">
                  <span className="block text-[10px] uppercase tracking-widest text-brand-gold">
                    Modulo {mi + 1}
                  </span>
                  <span className="block text-sm font-semibold text-brand-ink leading-snug">
                    {mod.title}
                  </span>
                  <span className="block text-[11px] text-brand-muted mt-0.5">
                    {done}/{mod.lessons.length} lezioni
                    {mod.has_intermediate_test && " · 1 test"}
                  </span>
                </span>
                {isOpen ? (
                  <ChevronUp size={15} className="text-brand-blue mt-1 shrink-0" />
                ) : (
                  <ChevronDown size={15} className="text-brand-muted mt-1 shrink-0" />
                )}
              </button>

              {isOpen && (
                <div className="pb-2">
                  {mod.lessons.map((l) => {
                    const isCurrent = l.id === currentLessonId;
                    const state = lessonState(l, isCurrent);
                    const disabled = l.is_locked;

                    return (
                      <button
                        key={l.id}
                        type="button"
                        disabled={disabled}
                        onClick={() =>
                          !disabled && navigate(`/studente/corsi/${courseId}/lezioni/${l.id}`)
                        }
                        title={disabled ? lessonHint(l) : l.title}
                        data-testid={`outline-lesson-${l.id}`}
                        className={
                          "w-full text-left pl-6 pr-4 py-2 flex items-start gap-2.5 border-l-2 transition-colors " +
                          (isCurrent
                            ? "border-l-brand-blue bg-brand-blue/5"
                            : "border-l-transparent hover:bg-brand-bg") +
                          (disabled ? " cursor-not-allowed" : "")
                        }
                      >
                        <span className="mt-0.5">
                          <StepIcon state={state} />
                        </span>
                        <span className="min-w-0">
                          <span
                            className={
                              "block text-[13px] leading-snug " +
                              (isCurrent
                                ? "font-semibold text-brand-blue"
                                : disabled
                                ? "text-brand-muted"
                                : "text-brand-ink")
                            }
                          >
                            {l.title}
                          </span>
                          <span className="block text-[11px] text-brand-muted mt-0.5">
                            {lessonHint(l)}
                          </span>
                        </span>
                      </button>
                    );
                  })}

                  {/* Test del modulo: è un passaggio del percorso, non un extra */}
                  {test && (
                    <button
                      type="button"
                      disabled={!test.clickable || startingTest}
                      onClick={() => test.clickable && onStartTest?.(mod.id)}
                      data-testid={`outline-test-${mod.id}`}
                      className={
                        "w-full text-left pl-6 pr-4 py-2 flex items-start gap-2.5 border-l-2 border-l-transparent transition-colors " +
                        (test.clickable ? "hover:bg-brand-bg" : "cursor-not-allowed")
                      }
                    >
                      <span className="mt-0.5">
                        {startingTest && test.clickable ? (
                          <Loader2 size={16} className="animate-spin text-brand-blue" />
                        ) : test.state === "completed" ? (
                          <CheckCircle2 size={17} className="text-emerald-600 shrink-0" />
                        ) : test.state === "locked" ? (
                          <Lock size={16} className="text-brand-muted shrink-0" />
                        ) : (
                          <ClipboardList size={16} className="text-brand-blue shrink-0" />
                        )}
                      </span>
                      <span className="min-w-0">
                        <span
                          className={
                            "block text-[13px] leading-snug font-semibold " +
                            (test.state === "locked" ? "text-brand-muted" : "text-brand-ink")
                          }
                        >
                          Test del modulo
                        </span>
                        <span className="block text-[11px] text-brand-muted mt-0.5">{test.hint}</span>
                      </span>
                    </button>
                  )}
                </div>
              )}
            </div>
          );
        })}

        {/* Test finale: chiude il percorso */}
        {summary.has_final_test && (
          <div className="px-4 py-3 border-b border-brand-line bg-brand-bg">
            <div className="flex items-start gap-2.5">
              <span className="mt-0.5">
                {summary.final_test_passed ? (
                  <CheckCircle2 size={17} className="text-emerald-600" />
                ) : summary.final_test_unlocked ? (
                  <Trophy size={16} className="text-brand-gold" />
                ) : (
                  <Lock size={16} className="text-brand-muted" />
                )}
              </span>
              <div className="min-w-0">
                <div
                  className={
                    "text-[13px] font-semibold leading-snug " +
                    (summary.final_test_unlocked || summary.final_test_passed
                      ? "text-brand-ink"
                      : "text-brand-muted")
                  }
                >
                  Test finale del corso
                </div>
                <div className="text-[11px] text-brand-muted mt-0.5">
                  {summary.final_test_passed
                    ? "Superato"
                    : summary.final_test_unlocked
                    ? "Disponibile"
                    : "Supera tutti i test dei moduli"}
                </div>
                {(summary.final_test_unlocked || summary.final_test_passed) && (
                  <button
                    type="button"
                    onClick={() => navigate(`/studente/corsi/${courseId}`)}
                    data-testid="outline-goto-final"
                    className="text-[11px] text-brand-blue hover:underline underline-offset-4 mt-1"
                  >
                    Vai al test finale →
                  </button>
                )}
              </div>
            </div>
          </div>
        )}
      </nav>
    </div>
  );
}
